import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { translate } from "../Common/translations";

export type ParentInfo = {
    id: number;
    name: string;
};

export type ContactData = {
    email1?: string;
};

export const AcademyApprovalModeEnum = {
    Auto: "auto",
    Manual: "manual",
} as const;

export type AcademyApprovalMode = (typeof AcademyApprovalModeEnum)[keyof typeof AcademyApprovalModeEnum];

export function translateAcademyApprovalMode(mode: AcademyApprovalMode): string {
    return translate("academy_approval_mode." + mode);
}

export type PublicAcademy = {
    id: number;
    name: string;
    country: string | null;
    city: string | null;
    street: string | null;
    description: string | null;
    userId: number | null;
    parentInfo: ParentInfo | null;
    logoImage: string | null;
    headerImage: string | null;
    contactData: ContactData | null;
    approvalMode: AcademyApprovalMode;
    competitorsCount: number | null;
};

export type LegacyAcademy = {
    key: number;
    value: string;
};

export type GlobalAcademiesListingResponse = Array<{ key: number; value: string }>;

export const AcademyRoleEnum = {
    Manager: "manager",
    Coach: "coach",
    Member: "member",
} as const;

export type AcademyRole = (typeof AcademyRoleEnum)[keyof typeof AcademyRoleEnum];

export function translateAcademyRole(role: AcademyRole): string {
    return translate("academy_role." + role);
}

export const AcademyMemberStatusEnum = {
    Pending: "pending",
    Approved: "approved",
    Rejected: "rejected",
} as const;

export type AcademyMemberStatus = (typeof AcademyMemberStatusEnum)[keyof typeof AcademyMemberStatusEnum];

export function translateAcademyMemberStatus(status: AcademyMemberStatus): string {
    return translate("academy_member_status." + status);
}

export type AcademyMemberSummary = {
    id: number;
    userId: number | null;
    firstName: string;
    lastName: string;
    email: string;
    role: AcademyRole;
    status: AcademyMemberStatus;
    shadowMember: boolean;
    created: string;
    approvedAt: string | null;
    approvedBy: number | null;
};

export type AcademyMembersListing = {
    members: AcademyMemberSummary[];
    viewerRole: AcademyRole | null;
    canManage: boolean;
};

export type AssignMemberRoleRequest = {
    academyId: number;
    memberId: number;
    role: AcademyRole;
};

export type AddMemberRequest = {
    firstName: string;
    lastName: string;
    email: string;
    role: AcademyRole;
};

export type MembershipDecision = "approved" | "rejected";

export type DecideMembershipRequest = {
    decision: MembershipDecision;
};

export const JoinModeEnum = {
    None: "none",
    Optional: "optional",
    Mandatory: "mandatory",
    Blocked: "blocked",
} as const;

export type JoinMode = (typeof JoinModeEnum)[keyof typeof JoinModeEnum];

export type MyAcademyMembership = {
    joinMode: JoinMode;
    role: AcademyRole | null;
    status: AcademyMemberStatus | null;
};

export type BrowseAcademyEntry = {
    id: number;
    name: string;
    city: string | null;
    country: string | null;
    membersCount: number;
    isMine: boolean;
    viewerRole: AcademyRole | null;
    viewerStatus: AcademyMemberStatus | null;
    logoImage: string | null;
};

export type BrowseAcademiesListing = {
    all: BrowseAcademyEntry[];
};

export type UpdateApprovalModeRequest = {
    approvalMode: AcademyApprovalMode;
};

export type UpdateAcademyCommand = {
    id: number;
    name: string;
    country: string | null;
    street: string | null;
    city: string | null;
    description: string | null;
    contactData: ContactData | null;
    parentId: number | null;
    approvalMode: AcademyApprovalMode | null;
};

export default {
    getGlobalAcademies(): Promise<GlobalAcademiesListingResponse> {
        return promiseRequest<GlobalAcademiesListingResponse>({
            url: `/api/academies`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get global academies listing."));
    },
    /**
     * @deprecated List of academies are not created anymore with context of an event. "group_id" concept is neglected.
     */
    getAcademies(eventId): Promise<LegacyAcademy[]> {
        return promiseRequest<LegacyAcademy[]>({
            url: `/api/events/${eventId}/academies`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get academies for event."));
    },
    createAcademyGlobal(name, country, userId) {
        return promiseRequest({
            url: `/api/academies`,
            method: "post",
            json: {
                name: name,
                country: country,
                userId: userId,
            },
        }).then(resolveWithBody("Could not create new academy."));
    },
    updateAcademy(userId: number, data: UpdateAcademyCommand): Promise<void> {
        return promiseRequest({
            url: `/api/users/${userId}/academies/${data.id}`,
            method: "put",
            json: data,
        }).then(resolveNoContent("Could not update academy."));
    },
    updateApprovalMode(academyId: number, request: UpdateApprovalModeRequest): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/actions/update-approval-mode`,
            method: "put",
            json: request,
        }).then(resolveNoContent("Could not update academy approval mode."));
    },
    uploadAcademyImage(userId, academyId, type, file) {
        const formData = new FormData();
        formData.append("type", type);
        formData.append("file", file, file.name);

        return promiseRequest({
            url: `/api/users/${userId}/academies/${academyId}/images`,
            method: "POST",
            body: formData,
            headers: {
                Accept: "application/json",
            },
        }).then(resolveNoContent());
    },
    deleteAcademyImage(userId, academyId, type) {
        return promiseRequest({
            url: `/api/users/${userId}/academies/${academyId}/images/${type}`,
            json: true,
            method: "DELETE",
        }).then(resolveNoContent());
    },
    getPublicAcademy(academyId, includeCompetitorsCount = false): Promise<PublicAcademy> {
        return promiseRequest<PublicAcademy>({
            url: `/api/academies/${academyId}${includeCompetitorsCount ? "?includeCompetitorsCount=1" : ""}`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get public academy."));
    },
    revealAcademyEmail(academyId) {
        return promiseRequest({
            url: `/api/academies/${academyId}/reveal-email`,
            method: "post",
            json: true,
        }).then(resolveWithBody("Could not reveal academy email."));
    },
    getMembers(academyId: number): Promise<AcademyMembersListing> {
        return promiseRequest<AcademyMembersListing>({
            url: `/api/academies/${academyId}/members`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get academy members."));
    },
    assignMemberRole(academyId: number, memberId: number, role: AcademyRole): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/members/${memberId}/actions/assign-role`,
            method: "put",
            json: {
                academyId: academyId,
                memberId: memberId,
                role: role,
            } satisfies AssignMemberRoleRequest,
        }).then(resolveNoContent("Could not assign academy member role."));
    },
    addMember(academyId: number, firstName: string, lastName: string, email: string, role: AcademyRole): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/actions/add-member`,
            method: "post",
            json: {
                firstName: firstName,
                lastName: lastName,
                email: email,
                role: role,
            } satisfies AddMemberRequest,
        }).then(resolveNoContent("Could not add academy member."));
    },
    join(academyId: number): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/actions/join`,
            method: "post",
            json: true,
        }).then(resolveNoContent("Could not join academy."));
    },
    getMyMembership(academyId: number): Promise<MyAcademyMembership> {
        return promiseRequest<MyAcademyMembership>({
            url: `/api/academies/${academyId}/actions/my-membership`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get academy membership."));
    },
    getBrowseAcademies(): Promise<BrowseAcademiesListing> {
        return promiseRequest<BrowseAcademiesListing>({
            url: `/api/academies/browse`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get browsable academies."));
    },
    getAvailableCountries(): Promise<string[]> {
        return promiseRequest<string[]>({
            url: `/api/academies/available-countries`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not get available academy countries."));
    },
    decide(academyId: number, memberId: number, decision: MembershipDecision): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/members/${memberId}/actions/decide`,
            method: "put",
            json: {
                decision: decision,
            } satisfies DecideMembershipRequest,
        }).then(resolveNoContent("Could not decide on academy membership."));
    },
    removeMember(academyId: number, memberId: number): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/members/${memberId}`,
            method: "delete",
            json: true,
        }).then(resolveNoContent("Could not remove academy member."));
    },
    leaveAcademy(academyId: number): Promise<void> {
        return promiseRequest({
            url: `/api/academies/${academyId}/actions/leave`,
            method: "post",
            json: true,
        }).then(resolveNoContent("Could not leave academy."));
    },
};
